/**
 * Demo Mode Utilities
 * Anonymizes sensitive data for convention demos
 */

// Demo mode is disabled - always show real data
export const isDemoMode = () => {
  return false;
};

// Fake data pools
const fakeNames = [
  'John Smith', 'Jane Doe', 'Mike Johnson', 'Sarah Williams',
  'Robert Brown', 'Emily Davis', 'David Miller', 'Lisa Wilson',
  'James Taylor', 'Mary Anderson', 'Chris Martinez', 'Jennifer Lee'
];

const fakeStreets = [
  '123 Main Street', '456 Oak Avenue', '789 Elm Drive',
  '321 Pine Road', '654 Maple Lane', '987 Cedar Court',
  '147 Birch Way', '258 Willow Path', '369 Spruce Circle'
];

const fakeCities = [
  { city: 'Springfield', state: 'IL' },
  { city: 'Riverside', state: 'CA' },
  { city: 'Lakeside', state: 'FL' },
  { city: 'Hillview', state: 'TX' },
  { city: 'Greenville', state: 'NC' }
];

const fakePhones = [
  '(555) 123-4567', '(555) 234-5678', '(555) 345-6789',
  '(555) 456-7890', '(555) 567-8901', '(555) 678-9012'
];

const fakeCompanies = [
  'Acme Pest Control', 'Summit Services', 'Peak Solutions',
  'Precision Exterminators', 'Guardian Pest Management', 'Elite Home Services',
  'Fortress Pest Defense', 'Premier Exterminators', 'Apex Pest Solutions',
  'ProGuard Services', 'Shield Pest Control', 'Keystone Exterminators'
];

// Generate consistent fake data based on real data hash
const hashString = (str) => {
  if (!str) return 0;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

/**
 * Anonymize a person's name
 */
export const anonymizeName = (name) => {
  if (!isDemoMode() || !name) return name;
  const hash = hashString(name);
  return fakeNames[hash % fakeNames.length];
};

/**
 * Anonymize a phone number
 */
export const anonymizePhone = (phone) => {
  if (!isDemoMode() || !phone) return phone;
  const hash = hashString(phone);
  return fakePhones[hash % fakePhones.length];
};

/**
 * Anonymize a company name
 */
export const anonymizeCompany = (companyName) => {
  if (!isDemoMode() || !companyName) return companyName;
  const hash = hashString(companyName);
  return fakeCompanies[hash % fakeCompanies.length];
};

/**
 * Anonymize an address
 */
export const anonymizeAddress = (address) => {
  if (!isDemoMode()) return address;

  if (!address) {
    return address;
  }

  const hash = hashString(address.address1 || '');
  const cityHash = hashString(address.city || '');

  const fakeStreet = fakeStreets[hash % fakeStreets.length];
  const fakeCity = fakeCities[cityHash % fakeCities.length];

  return {
    ...address,
    address1: fakeStreet,
    address2: '',
    city: fakeCity.city,
    state: fakeCity.state,
    zip: '12345'
  };
};

/**
 * Anonymize message content (optional - for sensitive instructions)
 */
export const anonymizeMessage = (message) => {
  if (!isDemoMode() || !message) return message;

  // Replace potentially sensitive patterns
  let anonymized = message;

  // Replace phone numbers
  anonymized = anonymized.replace(/\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g, '(555) 123-4567');

  // Replace email addresses
  anonymized = anonymized.replace(/[\w.-]+@[\w.-]+\.\w+/g, 'demo@example.com');

  // Replace common address patterns
  anonymized = anonymized.replace(/\d+\s+[\w\s]+(?:Street|St|Avenue|Ave|Road|Rd|Drive|Dr|Lane|Ln|Court|Ct|Circle|Cir|Boulevard|Blvd)/gi, '123 Main Street');

  return anonymized;
};

/**
 * Anonymize technician info
 */
export const anonymizeTechnician = (technician) => {
  if (!isDemoMode() || !technician) return technician;

  return {
    ...technician,
    name: anonymizeName(technician.name)
  };
};

/**
 * Anonymize contact info
 */
export const anonymizeContact = (contact) => {
  if (!isDemoMode() || !contact) return contact;

  return {
    ...contact,
    name: anonymizeName(contact.name),
    phone: anonymizePhone(contact.phone),
    location: anonymizeAddress(contact.location || {})
  };
};

/**
 * Anonymize entire conversation
 */
export const anonymizeConversation = (conversation) => {
  if (!isDemoMode() || !conversation) return conversation;

  return {
    ...conversation,
    contact: anonymizeContact(conversation.contact),
    technician: conversation.technician ? anonymizeTechnician(conversation.technician) : null,
    lastMessage: conversation.lastMessage ? {
      ...conversation.lastMessage,
      body: anonymizeMessage(conversation.lastMessage.body)
    } : null,
    appointment: conversation.appointment ? {
      ...conversation.appointment,
      metadata: conversation.appointment.metadata ? {
        ...conversation.appointment.metadata,
        ServiceInstructions: anonymizeMessage(conversation.appointment.metadata.ServiceInstructions || ''),
        LocationInstructions: anonymizeMessage(conversation.appointment.metadata.LocationInstructions || '')
      } : null
    } : null
  };
};
